import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApiClientError,
  type AlternativeMode,
  type ApiClientErrorCode,
  type Paint,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { PaintCardView, type PaintCardState } from './PaintCardView';

/**
 * Container Карточка краски (PRD §6 Block 3 Phase 4). См. ADR-0005.
 *
 * Loads paint via `apiClient.paint.get(globalId)`. Wires:
 *   - alternatives mood click → navigate `/paint/:globalId/alternatives?mode=...`.
 *   - share → `navigator.share` + clipboard fallback (симметрия с DecorCard).
 *   - back → catalog `/paint`.
 *
 * P-001 verbatim: react-router-dom v7 уже декодирует path params, поэтому
 * `params.globalId` приходит сюда уже verbatim Global_ID — включая `.jpg`
 * суффикс per ADR-0010 §4. Передаём в apiClient как есть; apiClient
 * encodeURIComponent-ит обратно при создании URL.
 */
export function PaintCard() {
  const navigate = useNavigate();
  const params = useParams<{ globalId: string }>();
  const globalId = params.globalId ?? '';

  const [state, setState] = useState<PaintCardState>('loading');
  const [paint, setPaint] = useState<Paint | undefined>(undefined);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!globalId) {
      setState('not-found');
      return;
    }
    setState('loading');
    setError(undefined);
    let cancelled = false;
    apiClient.paint.get(globalId).then(
      (p) => {
        if (cancelled) return;
        setPaint(p);
        setState('success');
      },
      (err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.code === 'NOT_FOUND') {
          setState('not-found');
          return;
        }
        // Не leak-аем String(err) в UI (security audit): non-ApiClientError → пустой message.
        const apiErr =
          err instanceof ApiClientError
            ? { code: err.code, message: err.message }
            : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
        setError(apiErr);
        setState('error');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [globalId]);

  function onAlternativeMode(mode: AlternativeMode) {
    navigate(`/paint/${encodeURIComponent(globalId)}/alternatives?mode=${mode}`);
  }

  /**
   * Phase 7 #3 паттерн (DecorCard): navigator.share where available; clipboard
   * fallback otherwise. Mobile + Edge desktop поддерживают share API,
   * Chrome/Firefox desktop — нет. Phase 8+ заменит alert на toast.
   */
  async function onShareClick() {
    if (!paint) return;
    const shareData = {
      title: `${paint.name} — ${paint.ralCode}`,
      text: `Краска «${paint.name}» (${paint.ralCode}, ${paint.brand})`,
      url: window.location.href,
    };
    const nav = navigator as Navigator & {
      share?: (data: ShareData) => Promise<void>;
    };
    if (nav.share) {
      try {
        await nav.share(shareData);
      } catch {
        // user cancelled — silent
      }
      return;
    }
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
      alert('Ссылка скопирована');
      return;
    }
    alert(`${shareData.text}\n${shareData.url}`);
  }

  function onBackToCatalog() {
    navigate('/paint');
  }

  return (
    <PaintCardView
      state={state}
      paint={paint}
      error={error}
      onAlternativeMode={onAlternativeMode}
      onShareClick={onShareClick}
      onBackToCatalog={onBackToCatalog}
    />
  );
}
